import React, { lazy } from 'react';
import {
  mdiAccountBadge,
  mdiAccountMultiple,
  mdiBank,
  mdiCheckboxMarkedCircleOutline,
  mdiClockOutline,
  mdiCurrencyUsd,
  mdiDesk,
  mdiFileDocumentEditOutline,
  mdiFormSelect,
  mdiFormatListBulleted,
  mdiHome,
  mdiOfficeBuilding,
  mdiResistor,
  mdiWindowShutter,
} from '@mdi/js';
import { CINCH_DRAW_REQUEST, ROLE_NAMES } from 'src/util';
import ClosedSterlingBrokerProperty from './ClosedBrokerProperty';

const LoanProgram = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/LoanProgram'),
);
const Prequal = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/Prequal'),
);
const LoanTerms = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/LoanTerms'),
);
const BorrowerInfo = lazy(
  () => import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo'),
);
const PersonalInformation = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/PersonalInformation'),
);
const IndividualSectionForPair = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/IndividualSectionForPair'),
);
const URLAAlternateNames = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/AlternateNames'),
);
const Residences = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/Residences'),
);
const MailingAddress = lazy(
  () =>
    import('src/components/loanOfficer/Loan//Forms/Shared/BorrowerInfo/MailingAddress'),
);
const ACHInfo = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/ACH'),
);
const FileContacts = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/FileContacts'),
);
const JuniorLienExceptionRequest = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/JuniorLien'),
);
const GoodFaithDeposit = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Sterling/GoodFaithDeposit'),
);
const AdditionalFields = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Sterling/AdditionalFields'),
);
const Expiration = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/Expiration'),
);
const DrawRequest = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/DrawRequest'),
);

const CommissionSheet = lazy(
  () =>
    import('src/components/loanOfficer/Loan/Forms/Sterling/CommissionSheet'),
);
const Servicing = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/Servicing'),
);
const Closing = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/Closing'),
);
const EntityInfo = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/EntityInfo'),
);
const PurchaseAdvice = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Sterling/PurchaseAdvice'),
);
const Itemization = lazy(
  () => import('src/components/loanOfficer/Loan/Forms/Shared/Itemization'),
);
export const formSections = [
  {
    title: 'Loan Program',
    route: 'loan-program',
    icon: mdiFormSelect,
    component: <LoanProgram />,
    requiresLoanProgram: false,
  },
  {
    title: 'Pre-qual',
    route: 'prequal-fnf',
    icon: mdiCheckboxMarkedCircleOutline,
    component: <Prequal />,
    requiresLoanProgram: true,
  },

  {
    title: 'Entity Info',
    route: 'entity-info',
    icon: mdiOfficeBuilding,
    component: <EntityInfo />,
    requiresLoanProgram: true,
  },
  {
    title: 'Request Exception',
    route: 'juniorlien-exception',
    icon: mdiCheckboxMarkedCircleOutline,
    component: <JuniorLienExceptionRequest />,
    requiresLoanProgram: true,
    juniorLienOnly: true,
  },
  {
    title: 'Loan Terms',
    route: 'loan-terms',
    icon: mdiCurrencyUsd,
    component: <LoanTerms />,
    requiresLoanProgram: true,
  },
  {
    title: 'Loan Contacts',
    route: 'loan-contacts',
    icon: mdiAccountBadge,
    component: <FileContacts />,
    requiresLoanProgram: true,
  },
  {
    title: 'Borrower Info',
    route: 'borrower-info',
    icon: mdiAccountMultiple,
    component: <BorrowerInfo />,
    requiresLoanProgram: true,
    applicationDependent: true,
    sections: [
      {
        title: 'Personal Information',
        route: 'information',
        component: (
          <IndividualSectionForPair
            Component={PersonalInformation}
            title='Personal Information'
          />
        ),
      },
      {
        title: 'Alternate Names',
        route: 'alternate-names',
        component: (
          <IndividualSectionForPair
            Component={URLAAlternateNames}
            title='Alternate Names'
          />
        ),
      },
      {
        title: 'Residences',
        route: 'residences',
        component: (
          <IndividualSectionForPair Component={Residences} title='Residences' />
        ),
      },
      {
        title: 'Mailing Address',
        route: 'mailing-address',
        component: (
          <IndividualSectionForPair
            Component={MailingAddress}
            title='Mailing Address'
          />
        ),
      },
    ],
  },

  {
    title: 'ACH Info',
    route: 'ach-info',
    icon: mdiBank,
    component: <ACHInfo />,
    requiresLoanProgram: true,
    includeForNQM: true,
  },
  {
    title: 'Additional Fields',
    route: 'fnf-additional-fields',
    icon: mdiFileDocumentEditOutline,
    component: <AdditionalFields />,
  },
  {
    title: 'Good Faith Deposit',
    route: 'good-faith',
    icon: mdiCurrencyUsd,
    component: <GoodFaithDeposit />,
  },
  {
    title: 'Expiration',
    route: 'expiration',
    icon: mdiClockOutline,
    component: <Expiration />,
  },

  {
    title: 'Commission',
    route: 'sterling-commission',
    icon: mdiCurrencyUsd,
    component: <CommissionSheet />,
    includeForSterling: true,
    roleFilter: [
      ROLE_NAMES.ADMINISTRATOR,
      ROLE_NAMES.STERLINGCONSTRUCTIONMANAGER,
    ],
  },
  {
    title: 'Draw Request',
    route: 'draw-request',
    icon: mdiResistor,
    component: <DrawRequest />,
    featureFlag: CINCH_DRAW_REQUEST,
  },
  {
    title: 'Closing',
    route: 'sterling-closing',
    icon: mdiWindowShutter,
    component: <Closing />,
  },
  {
    title: 'Servicing',
    route: 'sterling-servicing',
    icon: mdiDesk,
    component: <Servicing />,
    roleFilter: [ROLE_NAMES.ADMINISTRATOR, ROLE_NAMES.STERLINGSERVICING],
  },
  {
    title: 'Purchase Advice',
    route: 'sterling-purchasing',
    icon: mdiCurrencyUsd,
    component: <PurchaseAdvice />,
  },
  {
    title: 'Itemization',
    route: 'itemization',
    icon: mdiFormatListBulleted,
    component: <Itemization />,
  },
];

export const closedSterlingBrokerFormSections = [
  {
    title: 'Loan Program',
    route: 'loan-program',
    icon: mdiFormSelect,
    component: <LoanProgram />,
    requiresLoanProgram: false,
  },
  {
    title: 'Property',
    route: 'closed-sterling-broker-property',
    icon: mdiHome,
    component: <ClosedSterlingBrokerProperty />,
  },
  ...formSections.filter((section) =>
    ['borrower-info', 'sterling-commission'].includes(section.route),
  ),
];
